<template>
  <ModalContainer
    :is-show-modal="isShowModal"
    :styles="{ 'map-filter-modal': true }"
    @close="closeModal"
  >
    <template #header>
      <div>
        {{ $t("Filters") }}
        <span v-if="filterNumber">({{ filterNumber }})</span>
      </div>
      <BaseButton small @click="closeModal">{{ $t("Apply") }}</BaseButton>
    </template>

    <div v-if="filterNumber">
      <BaseButton outlined @click="resetFilters">{{
        $t("Reset filters")
      }}</BaseButton>
    </div>

    <div>
      <p>{{ $t("Membership type") }}</p>
      <ul>
        <li
          v-for="(level, index) in availableLevelsGetter"
          :key="`level-${index}`"
          @click="changeLevel(level)"
        >
          <span>{{ level.value }}</span>
          <CircleCheckbox :checked="level.id === selectedSubscriptionLevel.id" disabled />
        </li>
      </ul>
    </div>

    <div>
      <p>{{ $t("Activities") }}</p>
      <ul>
        <li
          v-for="(activity, index) in activityList"
          :key="`activity-${index}`"
          :class="{ unavailable: activity.isAvailable === false }"
          @click="setMapFilterActivities(activity)"
        >
          <span>{{ activity.value }}</span>
          <BaseIcon v-if="activity.isAvailable === false" icon="info" />
          <CircleCheckbox
            v-else
            :checked="isChecked(activity, 'Activities')"
            disabled
          />
        </li>
        <li
          class="show-all"
          @click="
            showAllItems(
              'Activities',
              filtersList.activities,
              setMapFilterActivities
            )
          "
        >
          <span>{{ $t("Show all") }}</span>
          <span>
            <BaseIcon
              icon="arrow"
              height="24"
              widht="24" />
          </span>
        </li>
      </ul>
    </div>

    <div>
      <p>{{ $t("More") }}</p>
      <ul>
        <li
          v-for="(tag, index) in tagList"
          :key="`tag-${index}`"
          :class="{ unavailable: tag.isAvailable === false }"
          @click="setMapFilterTags(tag)"
        >
          <span>{{ tag.value }}</span>
          <BaseIcon v-if="tag.isAvailable === false" icon="info" />
          <CircleCheckbox
            v-else
            :checked="isChecked(tag, 'More')"
            disabled />
        </li>
        <li
          class="show-all"
          @click="
            showAllItems('More', filtersList.tags, setMapFilterTags)
          "
        >
          <span>{{ $t("Show all") }}</span>
          <span>
            <BaseIcon
              icon="arrow"
              height="24"
              widht="24" />
          </span>
        </li>
      </ul>
    </div>

    <div>
      <p>{{ $t("City") }}</p>
      <ul>
        <li
          v-for="(slicedCity, index) in citiesSlice"
          :key="`city-${index}`"
          :class="{ unavailable: slicedCity.isAvailable === false || isCityButtonClicked }"
          @click="changeCity(slicedCity)"
        >
          <span>{{ slicedCity.value }}</span>
          <BaseIcon v-if="slicedCity.isAvailable === false" icon="info" />
          <CircleCheckbox
            v-else
            :checked="isChecked(slicedCity, 'City')"
            disabled />
        </li>
        <li
          class="show-all"
          @click="
            showAllItems('City', filtersList.cities, changeCity)
          "
        >
          <span>{{ $t("Show all") }}</span>
          <span>
            <BaseIcon
              icon="arrow"
              height="24"
              widht="24" />
          </span>
        </li>
      </ul>
    </div>

    <ModalContainer
      :is-show-modal="allItemObject.allItems.length > 0"
      :styles="{ 'map-filter-modal': true }"
      @close="closeAllItems"
    >
      <template #header>
        <div>{{ $t(allItemObject.filterName) }}</div>
        <BaseButton small @click="closeAllItems">{{ $t("Apply") }}</BaseButton>
      </template>
      <div>
        <p></p>
        <ul>
          <li
            v-for="item in allItemObject.allItems"
            :key="`${allItemObject.filterName}-${item.id}`"
            :class="{ unavailable: item.isAvailable === false || isCityButtonClicked }"
            @click="allItemObject.changeFilterFunction(item)"
          >
            <span>{{ item.value }}</span>
            <BaseIcon v-if="item.isAvailable === false" icon="info" />
            <CircleCheckbox
              v-else
              :checked="isChecked(item, allItemObject.filterName)"
              disabled
            />
          </li>
        </ul>
      </div>
    </ModalContainer>
  </ModalContainer>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import { storeToRefs } from "pinia";

import { useMapFilterStore } from "~/stores/mapFilter";
import { IMapFiltersResponse } from "~/types/map.interface";
import { type BaseSelectOption } from "~/components/input/BaseSelect.vue";

import ModalContainer from "~/components/modals/ModalContainer.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import CircleCheckbox from "~/components/input/CircleCheckbox.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import { mapSubscriptionModelToLevels } from "~/constants";

const props = defineProps({
  isShowModal: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  filtersList: {
    type: Object as PropType<IMapFiltersResponse>,
    required: true,
  },
  isCityButtonClicked: {
    type: Boolean as PropType<boolean>,
  }
}); 

const emit = defineEmits([
  "update:searchInput",
  "update:isShowModal",
  "selectFacilityObject",
  "changeLevel",
  "changeCity",
]); 

const mapFilterStore = useMapFilterStore();

const {
  availableLevelsGetter,
  activities,
  tags,
  city,
  selectedLevel,
  subscriptionModel,
} = storeToRefs(mapFilterStore);

const {
  setMapFilterActivities,
  setMapFilterTags,
  resetMapFilterTags,
  resetMapFilterActivities,
} = mapFilterStore;

const selectedFiltersStore = {
  City: city,
  Activities: activities,
  More: tags,
  Level: selectedLevel,
};

type SelectedFiltersKey = keyof typeof selectedFiltersStore;

const selectedSubscriptionLevel = computed(() => selectedLevel.value[mapSubscriptionModelToLevels[subscriptionModel.value]]);

const allItemObject = reactive({
  filterName: "" as SelectedFiltersKey,
  allItems: [] as BaseSelectOption[],
  changeFilterFunction: (_: BaseSelectOption) => {},
});

function showAllItems(
  filterName: SelectedFiltersKey,
  allItems: BaseSelectOption[],
  changeFilterFunction: (item: BaseSelectOption) => void
) {
  allItemObject.filterName = filterName;
  allItemObject.changeFilterFunction = changeFilterFunction;
  allItemObject.allItems = allItems;
}
function closeAllItems() {
  allItemObject.allItems = [];
  allItemObject.filterName = "" as SelectedFiltersKey;
  allItemObject.changeFilterFunction = () => {};
}

function isChecked(currentItem: BaseSelectOption, key: SelectedFiltersKey) {
  if (Array.isArray(selectedFiltersStore[key].value)) {
    return selectedFiltersStore[key].value.some((e) => e?.id === currentItem?.id);
  } else {
    return selectedFiltersStore[key].value?.id === currentItem?.id;
  }
}

const activityList = computed(() => {
  return props.filtersList.activities.slice(0, 3);
});
const tagList = computed(() => {
  return props.filtersList.tags.slice(0, 3);
});
const filterNumber = computed(() => {
  return tags.value.length + activities.value.length;
});

const citiesSlice = computed(() => {
  return props.filtersList.cities.slice(0, 3);
});

function changeLevel(level: BaseSelectOption) {
  emit("changeLevel", level);
};

function changeCity(city: BaseSelectOption) {
  if (!props.isCityButtonClicked)
    emit("changeCity", city);
};

function closeModal() {
  emit("update:isShowModal", false);
};

function resetFilters() {
  resetMapFilterTags();
  resetMapFilterActivities();
};
</script>
